import { LitElement, html, css } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';

/**
 * pm-smart-pairs-modal
 * 
 * Componente Lit per il modale di gestione intelligente delle coppie.
 * Gestisce 3 modalità operative:
 * - CREATE: Genera tutte le coppie da zero
 * - COMPLETE: Accoppia solo i giocatori rimanenti
 * - RESET: Elimina e ricrea tutte le coppie
 * 
 * @example
 * <pm-smart-pairs-modal
 *   tournament-id="123"
 *   total-players="16"
 *   current-pairs="4"
 *   unpaired-count="8"
 *   action-url="/simulations/tournaments/123/generate-random-pairs/">
 * </pm-smart-pairs-modal>
 */
@customElement('pm-smart-pairs-modal')
export class PmSmartPairsModal extends LitElement {
    // Properties
    @property({ type: Number, attribute: 'tournament-id' }) tournamentId: number = 0;
    @property({ type: Number, attribute: 'total-players' }) totalPlayers: number = 0;
    @property({ type: Number, attribute: 'current-pairs' }) currentPairs: number = 0;
    @property({ type: Number, attribute: 'unpaired-count' }) unpairedCount: number = 0;
    @property({ type: String, attribute: 'action-url' }) actionUrl: string = '';

    // State
    @state() private operationMode: 'create' | 'complete' | 'reset' = 'create';
    @state() private balancePairs: boolean = true;
    @state() private balanceStrategy: 'complementary' | 'similar' | 'mixed' = 'complementary';

    static styles = css`
        :host {
            display: block;
        }

        .alert {
            padding: 0.75rem 1rem;
            margin-bottom: 1rem;
            border: 1px solid transparent;
            border-radius: 0.375rem;
        }

        .alert-info {
            color: #055160;
            background-color: #cff4fc;
            border-color: #b6effb;
        }

        .alert-warning {
            color: #664d03;
            background-color: #fff3cd;
            border-color: #ffecb5;
        }

        .alert-success {
            color: #0f5132;
            background-color: #d1e7dd;
            border-color: #badbcc;
        }

        [data-bs-theme="dark"] .alert-info {
            color: #6edff6;
            background-color: rgba(13, 202, 240, 0.15);
            border-color: rgba(13, 202, 240, 0.3);
        }

        [data-bs-theme="dark"] .alert-warning {
            color: #ffda6a;
            background-color: rgba(255, 193, 7, 0.15);
            border-color: rgba(255, 193, 7, 0.3);
        }

        [data-bs-theme="dark"] .alert-success {
            color: #75b798;
            background-color: rgba(25, 135, 84, 0.15);
            border-color: rgba(25, 135, 84, 0.3);
        }

        .alert ul {
            margin-bottom: 0;
            margin-top: 0.5rem;
            padding-left: 1.5rem;
        }

        .options-box {
            border: 1px solid #dee2e6;
            border-radius: 0.375rem;
            padding: 0.75rem;
            margin-bottom: 1rem;
        }

        [data-bs-theme="dark"] .options-box {
            border-color: #495057;
        }

        .options-box h6 {
            margin: 0 0 0.75rem 0;
            font-size: 1rem;
            font-weight: 600;
        }

        .form-check {
            margin-bottom: 0.5rem;
            padding-left: 1.5em;
        }

        .form-check:last-child {
            margin-bottom: 0;
        }

        .form-check-input {
            width: 1.25rem;
            height: 1.25rem;
            margin-top: 0;
            margin-left: -1.5em;
            cursor: pointer;
        }

        .form-check-label {
            cursor: pointer;
        }

        .form-text {
            margin-top: 0.25rem;
            font-size: 0.875em;
            color: #6c757d;
            display: block;
        }

        .badge {
            display: inline-block;
            padding: 0.35em 0.65em;
            font-size: 0.75em;
            font-weight: 700;
            line-height: 1;
            color: #fff;
            text-align: center;
            white-space: nowrap;
            vertical-align: baseline;
            border-radius: 0.375rem;
        }

        .badge.bg-success {
            background-color: #198754;
        }

        .text-muted {
            color: #6c757d !important;
        }

        .footer-buttons {
            display: flex;
            gap: 0.5rem;
            justify-content: flex-end;
            margin-top: 1.5rem;
        }

        .btn {
            display: inline-block;
            padding: 0.375rem 0.75rem;
            font-size: 1rem;
            font-weight: 400;
            line-height: 1.5;
            text-align: center;
            text-decoration: none;
            vertical-align: middle;
            cursor: pointer;
            border: 1px solid transparent;
            border-radius: 0.375rem;
            transition: all 0.15s ease-in-out;
        }

        .btn-secondary {
            color: #fff;
            background-color: #6c757d;
            border-color: #6c757d;
        }

        .btn-secondary:hover {
            background-color: #5c636a;
            border-color: #565e64;
        }

        .btn-warning {
            color: #000;
            background-color: #ffc107;
            border-color: #ffc107;
        }

        .btn-warning:hover {
            background-color: #ffca2c;
            border-color: #ffc720;
        }

        .btn-info {
            color: #000;
            background-color: #0dcaf0;
            border-color: #0dcaf0;
        }

        .btn-info:hover {
            background-color: #31d2f2;
            border-color: #25cff2;
        }

        .btn-danger {
            color: #fff;
            background-color: #dc3545;
            border-color: #dc3545;
        }

        .btn-danger:hover {
            background-color: #bb2d3b;
            border-color: #b02a37;
        }

        .strategy-box {
            border: 1px solid #dee2e6;
            border-radius: 0.375rem;
            padding: 0.75rem;
            margin-top: 1rem;
        }

        [data-bs-theme="dark"] .strategy-box {
            border-color: #495057;
        }
    `;

    connectedCallback() {
        super.connectedCallback();
        this.updateOperationMode();
    }

    private updateOperationMode() {
        if (this.currentPairs === 0) {
            this.operationMode = 'create';
        } else if (this.unpairedCount > 0) {
            this.operationMode = 'complete';
        } else {
            this.operationMode = 'reset';
        }
    }

    private handleOperationModeChange(e: Event) {
        const input = e.target as HTMLInputElement;
        this.operationMode = input.value as 'create' | 'complete' | 'reset';
    }

    private handleBalancePairsChange(e: Event) {
        const input = e.target as HTMLInputElement;
        this.balancePairs = input.checked;
    }

    private handleBalanceStrategyChange(e: Event) {
        const input = e.target as HTMLInputElement;
        this.balanceStrategy = input.value as 'complementary' | 'similar' | 'mixed';
    }

    private handleSubmit(e: Event) {
        e.preventDefault();

        // Crea form e invia
        const form = document.createElement('form');
        form.method = 'POST';
        form.action = this.actionUrl;

        // CSRF token
        const csrfToken = document.querySelector('[name=csrfmiddlewaretoken]') as HTMLInputElement;
        if (csrfToken) {
            const csrfInput = document.createElement('input');
            csrfInput.type = 'hidden';
            csrfInput.name = 'csrfmiddlewaretoken';
            csrfInput.value = csrfToken.value;
            form.appendChild(csrfInput);
        }

        // Operation mode (solo per tracking interno, non serve al backend)
        const modeInput = document.createElement('input');
        modeInput.type = 'hidden';
        modeInput.name = 'operation_mode';
        modeInput.value = this.operationMode;
        form.appendChild(modeInput);

        // Regenerate flag (solo per reset)
        if (this.operationMode === 'reset') {
            const regenerateInput = document.createElement('input');
            regenerateInput.type = 'hidden';
            regenerateInput.name = 'regenerate';
            regenerateInput.value = 'true';
            form.appendChild(regenerateInput);
        }

        // Balance pairs
        if (this.balancePairs) {
            const balanceInput = document.createElement('input');
            balanceInput.type = 'hidden';
            balanceInput.name = 'balance_pairs';
            balanceInput.value = 'on';
            form.appendChild(balanceInput);

            // Balance strategy (solo se balance è attivo)
            const strategyInput = document.createElement('input');
            strategyInput.type = 'hidden';
            strategyInput.name = 'balance_strategy';
            strategyInput.value = this.balanceStrategy;
            form.appendChild(strategyInput);
        }

        document.body.appendChild(form);
        form.submit();
    }

    private handleCancel() {
        this.dispatchEvent(new CustomEvent('close', { bubbles: true, composed: true }));
    }

    private getAlertClass(): string {
        if (this.currentPairs === 0) return 'alert alert-info';
        if (this.unpairedCount > 0) return 'alert alert-warning';
        return 'alert alert-success';
    }

    private getPairsStatus(): string {
        if (this.currentPairs === 0) return 'Nessuna coppia formata';
        return `${this.currentPairs} coppie già formate`;
    }

    private getActionDescription(): string {
        if (this.currentPairs === 0) {
            const newPairs = Math.floor(this.totalPlayers / 2);
            return `Verranno create ${newPairs} nuove coppie`;
        }
        if (this.unpairedCount > 0) {
            const newPairs = Math.floor(this.unpairedCount / 2);
            return `${this.unpairedCount} giocatori da accoppiare (${newPairs} nuove coppie)`;
        }
        return 'Tutti i giocatori sono accoppiati';
    }

    private getSubmitButtonClass(): string {
        switch (this.operationMode) {
            case 'create': return 'btn btn-warning';
            case 'complete': return 'btn btn-info';
            case 'reset': return 'btn btn-danger';
        }
    }

    private getSubmitButtonText(): string {
        switch (this.operationMode) {
            case 'create': return 'Genera Coppie';
            case 'complete': return 'Accoppia Rimanenti';
            case 'reset': return 'Ricrea Tutte';
        }
    }

    render() {
        const showCreate = this.currentPairs === 0;
        const showComplete = this.currentPairs > 0 && this.unpairedCount > 0;
        const showReset = this.currentPairs > 0;

        return html`
            <!-- Info disponibilità -->
            <div class="${this.getAlertClass()}">
                <i class="fas fa-info-circle me-2"></i>
                <strong>Situazione attuale:</strong>
                <ul>
                    <li>${this.totalPlayers} giocatori iscritti</li>
                    <li>${this.getPairsStatus()}</li>
                    <li>${this.getActionDescription()}</li>
                </ul>
            </div>

            <!-- Modalità operativa -->
            <div class="options-box">
                <h6>
                    <i class="fas fa-cogs me-2"></i>Modalità Operativa
                </h6>

                ${showCreate ? html`
                    <div class="form-check">
                        <input 
                            class="form-check-input" 
                            type="radio" 
                            name="operation_mode"
                            id="create_all"
                            value="create"
                            .checked=${this.operationMode === 'create'}
                            @change=${this.handleOperationModeChange}>
                        <label class="form-check-label" for="create_all">
                            <strong>Genera tutte le coppie</strong>
                        </label>
                        <div class="form-text">Crea tutte le coppie da zero con bilanciamento intelligente</div>
                    </div>
                ` : ''}

                ${showComplete ? html`
                    <div class="form-check">
                        <input 
                            class="form-check-input" 
                            type="radio" 
                            name="operation_mode"
                            id="complete_remaining"
                            value="complete"
                            .checked=${this.operationMode === 'complete'}
                            @change=${this.handleOperationModeChange}>
                        <label class="form-check-label" for="complete_remaining">
                            <strong>Accoppia giocatori rimanenti</strong>
                        </label>
                        <div class="form-text">Crea coppie solo per i giocatori ancora non accoppiati</div>
                    </div>
                ` : ''}

                ${showReset ? html`
                    <div class="form-check">
                        <input 
                            class="form-check-input" 
                            type="radio" 
                            name="operation_mode"
                            id="reset_all"
                            value="reset"
                            .checked=${this.operationMode === 'reset'}
                            @change=${this.handleOperationModeChange}>
                        <label class="form-check-label" for="reset_all">
                            <strong>Ricrea tutte le coppie</strong>
                        </label>
                        <div class="form-text">Elimina le coppie esistenti e ricrea tutto da capo</div>
                    </div>
                ` : ''}
            </div>

            <!-- Opzioni di bilanciamento -->
            <div class="options-box">
                <h6>
                    <i class="fas fa-balance-scale me-2"></i>Opzioni di Bilanciamento
                </h6>

                <div class="form-check">
                    <input 
                        class="form-check-input" 
                        type="checkbox" 
                        id="balance_pairs"
                        .checked=${this.balancePairs}
                        @change=${this.handleBalancePairsChange}>
                    <label class="form-check-label" for="balance_pairs">
                        <strong>Bilanciare i livelli nelle coppie</strong>
                    </label>
                    <div class="form-text">Ottimizza l'accoppiamento per equilibrare i livelli di abilità</div>
                </div>
            </div>

            <!-- Strategia di bilanciamento -->
            ${this.balancePairs ? html`
                <div class="strategy-box">
                    <h6>
                        <i class="fas fa-magic me-2"></i>Strategia di Bilanciamento
                    </h6>

                    <div class="form-check">
                        <input 
                            class="form-check-input" 
                            type="radio" 
                            name="balance_strategy"
                            id="complementary"
                            value="complementary"
                            .checked=${this.balanceStrategy === 'complementary'}
                            @change=${this.handleBalanceStrategyChange}>
                        <label class="form-check-label" for="complementary">
                            <strong>Livelli Complementari</strong> <span class="badge bg-success">Consigliata</span>
                        </label>
                        <div class="form-text">Accoppia giocatori forti con deboli per massimo equilibrio competitivo</div>
                    </div>

                    <div class="form-check">
                        <input 
                            class="form-check-input" 
                            type="radio" 
                            name="balance_strategy"
                            id="similar"
                            value="similar"
                            .checked=${this.balanceStrategy === 'similar'}
                            @change=${this.handleBalanceStrategyChange}>
                        <label class="form-check-label" for="similar">
                            <strong>Livelli Simili</strong>
                        </label>
                        <div class="form-text">Accoppia giocatori dello stesso livello per uniformità</div>
                    </div>

                    <div class="form-check">
                        <input 
                            class="form-check-input" 
                            type="radio" 
                            name="balance_strategy"
                            id="mixed"
                            value="mixed"
                            .checked=${this.balanceStrategy === 'mixed'}
                            @change=${this.handleBalanceStrategyChange}>
                        <label class="form-check-label" for="mixed">
                            <strong>Strategia Mista</strong>
                        </label>
                        <div class="form-text">Combina entrambe le strategie per bilanciamento ottimale</div>
                    </div>
                </div>
            ` : ''}

            <!-- Footer buttons -->
            <div class="footer-buttons">
                <button 
                    type="button" 
                    class="btn btn-secondary"
                    @click=${this.handleCancel}>
                    <i class="fas fa-times me-2"></i>Annulla
                </button>
                <button 
                    type="button" 
                    class="${this.getSubmitButtonClass()}"
                    @click=${this.handleSubmit}>
                    <i class="fas fa-dice me-2"></i>${this.getSubmitButtonText()}
                </button>
            </div>
        `;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        'pm-smart-pairs-modal': PmSmartPairsModal;
    }
}
